import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useInitiateResetTransactionPinMutation, useCompleteResetTransactionPinMutation } from "../../../../../service/user-service";
import { FadeSlideSwitcher } from "../../../../../common/components/fade-slide-switcher/FadeSlideSwitcher";
import { ButtonWithLoadingState } from "../../../../../common/components/button-with-loading-state/ButtonWithLoadingState";
import { OtpVerificationCard } from "../../../../../common/components/otp-verification-card/OtpVerificationCard";
import { StepSuccessCard } from "../../../../../common/components/step-success-card/StepSuccessCard";
import { maskEmail } from "../../../onboarding/forgot-password/util";
import { getErrorMessage } from "../../../../../utils/error-handler";
import { describeError } from "../../../../../services/business-analytics/customFields";
import { trackResetTransactionPinFailed, trackResetTransactionPinSuccess } from "../../../../../services/business-analytics/transactionPinEvents";
import { RootState } from "../../../../../stores/store";
import { CodePinFields, CodePinFieldsHandle } from "../../../../../common/components/code-pin-fields/CodePinFields";
import styles from "./resetPinContent.module.scss";

type Phase = "otp" | "newPin";

export interface ResetPinContentProps {
	onPinReset: () => void;
}

export const ResetPinContent: React.FC<ResetPinContentProps> = ({ onPinReset }) => {
	const { t } = useTranslation();
	const email = useSelector((state: RootState) => state.user.email ?? "");

	const [phase, setPhase] = useState<Phase>("otp");
	const [otpCode, setOtpCode] = useState("");
	const [newPin, setNewPin] = useState("");
	const [confirmPin, setConfirmPin] = useState("");

	const newPinRef = useRef<CodePinFieldsHandle>(null);
	const confirmRef = useRef<CodePinFieldsHandle>(null);

	const [initiateReset, { isLoading: isInitiating }] = useInitiateResetTransactionPinMutation();
	const [completeReset, { isLoading: isCompleting }] = useCompleteResetTransactionPinMutation();

	useEffect(() => {
		initiateReset({})
			.unwrap()
			.catch((err: any) => toast.error(getErrorMessage(err?.data) || t("account.web.failedToSendOtpTryAgain")));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleResend = async () => {
		try {
			await initiateReset({}).unwrap();
			toast.success(t("account.web.codeResentSuccessfully"));
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data) || t("account.web.failedToResendOtp"));
		}
	};

	const handleOtpContinue = () => {
		setPhase("newPin");
	};

	const isSequentialPin = (value: string) => {
		const ascending = value.split("").every((d, i, arr) => i === 0 || Number(d) === Number(arr[i - 1]) + 1);
		const descending = value.split("").every((d, i, arr) => i === 0 || Number(d) === Number(arr[i - 1]) - 1);
		return ascending || descending;
	};

	const isRepeatedPin = (value: string) => value.split("").every(d => d === value[0]);



	const clearPinInputs = () => {
		setNewPin("");
		setConfirmPin("");
		newPinRef.current?.focus();
	};

	const handleSubmit = async () => {

		if (newPin.length < 4) {
			toast.error(t("pin.pleaseEnterAllPinDigits"));
			clearPinInputs();
			return;
		}

		if (isSequentialPin(newPin)) {
			toast.error(t("pin.pinCannotBeSequential"));
			clearPinInputs();
			return;
		}

		if (isRepeatedPin(newPin)) {
			toast.error(t("pin.pinCannotHaveRepeatedDigits"));
			clearPinInputs();
			return;

		}
		if (newPin !== confirmPin) {
			toast.error(t("account.web.pinsDoNotMatch"));
			clearPinInputs();
			return;
		}


		try {
			await completeReset({ pin: newPin, token: otpCode }).unwrap();
			trackResetTransactionPinSuccess();
			onPinReset();
		} catch (err: any) {
			trackResetTransactionPinFailed(describeError(err));
			toast.error(getErrorMessage(err?.data) || t("account.web.failedToResetPin"));
			clearPinInputs();
		}
	};

	const isSubmitEnabled = newPin.length === 4 && newPin === confirmPin;

	return (
		<div className={styles.content}>
			<FadeSlideSwitcher switchKey={phase}>
				<div>
					{phase === "otp" && (
						<>
							<OtpVerificationCard
								title={t("auth.otpVerification")}
								subtitle={
									<>
										{t("auth.enterOtpSentToEmail")} <span className="emailAccent">"{maskEmail(email)}"</span>
									</>
								}
								value={otpCode}
								onChange={setOtpCode}
								isVerifying={isInitiating}
								startTimerOnInit
								onResend={handleResend}
							/>
							<div className={styles.btnWrapper}>
								<ButtonWithLoadingState buttonText={t("common.actions.continueKey")} buttonPressed={otpCode.trim().length >= 6 ? handleOtpContinue : null} />
							</div>
						</>
					)}

					{phase === "newPin" && (
						<div className={styles.newPinStack}>
							<StepSuccessCard text={t("auth.otpVerified")} />

							<div className={styles.newPinCard}>
								<p className={styles.cardTitle}>{t("pin.createNewPinTitle")}</p>
								<p className={styles.cardSubtitle}>{t("pin.createNewPinSubtitle")}</p>

								<p className={styles.label}>{t("pin.newPin")}</p>
								<CodePinFields ref={newPinRef} numberOfFields={4} value={newPin} onChange={setNewPin} obscureText autoFocus onComplete={() => confirmRef.current?.focus()} />

								<p className={`${styles.label} ${styles.labelSpaced}`}>{t("pin.confirmPin")}</p>
								<CodePinFields ref={confirmRef} numberOfFields={4} value={confirmPin} onChange={setConfirmPin} obscureText />
							</div>

							<div className={styles.btnWrapper}>
								<ButtonWithLoadingState buttonText={t("common.actions.continueKey")} loading={isCompleting} buttonPressed={isSubmitEnabled ? handleSubmit : null} />
							</div>
						</div>
					)}
				</div>
			</FadeSlideSwitcher>
		</div>
	);
};
